import { effectScope, watch, type ObjectDirective } from "vue";
import { floatingTip, useDelayedTooltip } from "../composables/tooltip";

/**
 * Our tooltip on any element, in place of the browser's `title`: the same dark hint and delay as
 * IconButton's, on hover or keyboard focus, one at a time. The text also goes into the element as
 * screen-reader-only text, since the hint itself is never focusable. An empty text shows nothing.
 *
 *   <small v-tooltip="fxRateText()">≈ R$ 6,45 hoje</small>
 */
interface TipState {
  id: string;
  text: string;
  sr: HTMLSpanElement;
  dispose: () => void;
}
const states = new WeakMap<HTMLElement, TipState>();
let seq = 0;

function place(el: HTMLElement, state: TipState) {
  const r = el.getBoundingClientRect();
  floatingTip.value = {
    id: state.id,
    text: state.text,
    x: r.x + r.width / 2,
    below: r.bottom + 5,
    above: r.top - 5,
  };
}

/** Vue rewrites an element's text on update, which drops the hidden copy; put it back. */
function syncHidden(el: HTMLElement, state: TipState) {
  state.sr.textContent = state.text;
  if (state.text && state.sr.parentNode !== el) el.appendChild(state.sr);
  if (!state.text) state.sr.remove();
}

export const vTooltip: ObjectDirective<HTMLElement, string | null | undefined> =
  {
    mounted(el, { value }) {
      const sr = document.createElement("span");
      sr.className = "sr-only";
      const state: TipState = {
        id: `tip-${++seq}`,
        text: value ?? "",
        sr,
        dispose: () => undefined,
      };
      states.set(el, state);
      syncHidden(el, state);
      const scope = effectScope();
      scope.run(() => {
        const tip = useDelayedTooltip();
        watch(tip.visible, (shown) => {
          if (shown && state.text) place(el, state);
          else if (floatingTip.value?.id === state.id) floatingTip.value = null;
        });
        const enter = (e: PointerEvent) => {
          if (e.pointerType !== "touch" && state.text) tip.enter();
        };
        const focus = () => {
          if (state.text && el.matches(":focus-visible")) tip.enter();
        };
        const events: Array<[string, EventListener]> = [
          ["pointerenter", enter as EventListener],
          ["pointerleave", tip.leave],
          ["pointerdown", tip.cancel],
          ["focus", focus],
          ["blur", tip.leave],
        ];
        for (const [name, fn] of events) el.addEventListener(name, fn);
        state.dispose = () => {
          for (const [name, fn] of events) el.removeEventListener(name, fn);
        };
      });
      const dispose = state.dispose;
      state.dispose = () => {
        dispose();
        scope.stop();
      };
    },
    updated(el, { value }) {
      const state = states.get(el);
      if (!state) return;
      state.text = value ?? "";
      syncHidden(el, state);
      if (floatingTip.value?.id === state.id) {
        if (state.text)
          floatingTip.value = { ...floatingTip.value, text: state.text };
        else floatingTip.value = null;
      }
    },
    beforeUnmount(el) {
      const state = states.get(el);
      if (!state) return;
      state.dispose();
      state.sr.remove();
      if (floatingTip.value?.id === state.id) floatingTip.value = null;
      states.delete(el);
    },
  };
